import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { fmt } from "@tasduvar/engine";
import { parseTR } from "../lib/number";
import { Field } from "./Field";
import { useHighlight } from "../state/highlight";
import { useCadForm } from "./formContext";

export interface NumberFieldProps {
  id: string; label: string; unit?: string; symbol?: string;
  value: number; onChange: (n: number) => void;
  min?: number; max?: number; step?: number; digits?: number; disabled?: boolean; error?: string; hint?: string;
  /** Çizimdeki ölçü anahtarı (dimensionLines): alan odaklanınca o ölçü vurgulanır */
  dimKey?: string;
  autoFocus?: boolean;
  hideLabel?: boolean;
}

/** Sınır değerleri tam sayıysa ondalıksız yaz (89 → "89", 0,5 → "0,50"). */
function fmtBound(x: number, digits: number): string {
  return Number.isInteger(x) ? fmt(x, 0) : fmt(x, digits);
}

export function NumberField(p: NumberFieldProps): JSX.Element {
  const digits = p.digits ?? 2;
  const [text, setText] = useState(fmt(p.value, digits));
  const [localError, setLocalError] = useState<string | undefined>();
  const focused = useRef(false);
  // Dışarıdan gelen değer değişince (odakta değilken) metni yenile
  useEffect(() => { if (!focused.current) setText(fmt(p.value, digits)); }, [p.value, digits]);

  const validate = (n: number | null): string | undefined => {
    if (n === null) return "Geçerli bir sayı girin";
    if (p.min !== undefined && n < p.min) return `Değer en az ${fmtBound(p.min, digits)} olmalı`;
    if (p.max !== undefined && n > p.max) return `Değer en fazla ${fmtBound(p.max, digits)} olmalı`;
    return undefined;
  };
  const handleChange = (t: string): void => {
    setText(t);
    const n = parseTR(t);
    const err = validate(n);
    setLocalError(err);
    if (!err && n !== null) p.onChange(n);
  };
  const onBlur = (): void => {
    focused.current = false;
    if (localError) return;
    const n = parseTR(text);
    setText(fmt(n ?? p.value, digits));
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const inputs = Array.from(document.querySelectorAll<HTMLElement>("input,select,textarea,button")).filter((el) => !el.hasAttribute("disabled") && el.tabIndex >= 0);
    const i = inputs.indexOf(e.currentTarget);
    inputs[i + 1]?.focus();
  };
  const error = p.error ?? localError;
  const hot = useHighlight((s) => s.dimKey);
  const setDim = useHighlight((s) => s.setDim);
  const cad = useCadForm();
  const isHot = !!p.dimKey && hot === p.dimKey;
  return (
    <Field label={p.label} htmlFor={p.id} unit={p.unit} symbol={p.symbol} error={error} hint={p.hint} hideLabel={p.hideLabel} hot={isHot}>
      <input id={p.id} className={cad ? (p.hideLabel ? "cad-input w-full font-mono num" : "cad-prop-input") : `input num ${isHot ? "!border-soil ring-2 ring-soil/20" : ""}`} inputMode="decimal" value={text} disabled={p.disabled} aria-invalid={error ? "true" : undefined} autoFocus={p.autoFocus}
        onChange={(e) => handleChange(e.target.value)} onFocus={() => { focused.current = true; if (p.dimKey) setDim(p.dimKey); }}
        onMouseEnter={() => { if (p.dimKey && !focused.current) setDim(p.dimKey); }} onMouseLeave={() => { if (p.dimKey && !focused.current && useHighlight.getState().dimKey === p.dimKey) setDim(null); }}
        onBlur={() => { onBlur(); if (p.dimKey && useHighlight.getState().dimKey === p.dimKey) setDim(null); }} onKeyDown={onKeyDown} />
    </Field>
  );
}
